import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Palette, Globe, Upload, CheckCircle2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import TenantOnboardingTour from "@/components/TenantOnboardingTour";
import TenantOnboardingChecklist from "@/components/TenantOnboardingChecklist";

/**
 * `/tenant/settings` — buyer (tenant admin) edits their own branding: name,
 * primary color, logo, phone. Changes hit `/api/tenant/me` and are visible
 * instantly on their subdomain.
 */
export default function TenantSettings() {
  const { refresh: refreshBranding } = useBranding();
  const [tenant, setTenant] = useState(null);
  const [f, setF] = useState({ brand_name: "", primary_color: "#2E4FB8", logo_url: "", contact_phone: "", custom_domain: "", subdomain: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [subCheck, setSubCheck] = useState(null); // { available, clean, reason }
  const [checkingSub, setCheckingSub] = useState(false);

  const logoInputRef = useRef(null);
  const colorInputRef = useRef(null);

  useEffect(() => {
    api.get("/tenant/me").then(r => {
      setTenant(r.data);
      setF({
        brand_name: r.data.brand_name || "",
        primary_color: r.data.primary_color || "#2E4FB8",
        logo_url: r.data.logo_url || "",
        contact_phone: r.data.contact_phone || "",
        custom_domain: r.data.custom_domain || "",
        subdomain: r.data.subdomain || "",
      });
      // Auto-open the welcome modal on first visit (backend truth)
      if (!r.data.onboarding_completed) setShowTour(true);
    }).catch(() => toast.error("Could not load your tenant"));
  }, []);

  const markStep = async (step) => {
    try {
      const r = await api.post("/tenant/onboarding/step", { step });
      setTenant(prev => prev ? { ...prev, onboarding_steps: r.data.onboarding_steps, onboarding_completed: r.data.onboarding_completed } : prev);
    } catch {/* non-blocking */}
  };

  const save = async () => {
    setSaving(true);
    const colorChanged = !!tenant && tenant.primary_color !== f.primary_color;
    const subChanged = !!tenant && tenant.subdomain !== f.subdomain && f.subdomain;
    try {
      // Only send subdomain if the buyer actually changed it — avoid unnecessary uniqueness check on save
      const payload = { ...f };
      if (!subChanged) delete payload.subdomain;
      await api.patch("/tenant/me", payload);
      toast.success(subChanged
        ? `Site moved to ${f.subdomain}.dynamictax.online — old URL will stop working.`
        : "Branding saved — visible on your site immediately.");
      refreshBranding();
      const r = await api.get("/tenant/me");
      setTenant(r.data);
      if (colorChanged && !r.data?.onboarding_steps?.color) markStep("color");
      setSubCheck(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", files[0]);
    try {
      const r = await api.post("/tenant/me/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setF(prev => ({ ...prev, logo_url: r.data.logo_url }));
      toast.success("Logo uploaded. Hit save to apply.");
      if (!tenant?.onboarding_steps?.logo) markStep("logo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!tenant) return <Layout><div className="p-10 text-muted-foreground">Loading…</div></Layout>;

  const brandedUrl = `https://${tenant.custom_domain || `${tenant.subdomain}.dynamictax.online`}`;

  const focusLogo = () => {
    logoInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    logoInputRef.current?.click?.();
  };
  const focusColor = () => {
    colorInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => colorInputRef.current?.click?.(), 400);
  };
  const copyUrlAndMark = async () => {
    try { await navigator.clipboard.writeText(brandedUrl); } catch {}
    toast.success("URL copied — paste it wherever your clients hang out.");
    if (!tenant?.onboarding_steps?.share) markStep("share");
  };
  const dismissChecklist = () => markStep("skip");

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 lg:p-12 space-y-8" data-testid="tenant-settings-page">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={14} /> Back to dashboard</Link>

        {/* Onboarding: modal first, then persistent checklist */}
        <TenantOnboardingTour
          open={showTour}
          brandName={tenant.brand_name || "your practice"}
          onClose={() => setShowTour(false)}
          onSkip={() => { setShowTour(false); markStep("skip"); }}
        />
        <TenantOnboardingChecklist
          steps={tenant.onboarding_steps || { logo: false, color: false, share: false }}
          onDismiss={dismissChecklist}
          onFocusLogo={focusLogo}
          onFocusColor={focusColor}
          onCopyUrl={copyUrlAndMark}
        />

        <div>
          <div className="flex items-center gap-2 mb-1">
            <Palette size={20} className="text-primary" />
            <div className="label-caps">Your branding</div>
          </div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-3">Make it yours.</h1>
          <p className="text-muted-foreground">
            Update your logo, colors, and business name anytime. Changes go live on your site the moment you hit save.
          </p>
        </div>

        {/* Live preview */}
        <Card className="p-6" data-testid="tenant-preview">
          <div className="label-caps text-[10px] mb-3 text-primary">Live preview</div>
          <div className="flex items-center gap-4 p-6 rounded-lg border-2" style={{ borderColor: f.primary_color, backgroundColor: `${f.primary_color}10` }}>
            {f.logo_url ? (
              <img src={f.logo_url} alt="" className="h-14 w-14 object-contain rounded-md bg-white p-1 border border-border" />
            ) : (
              <div className="h-14 w-14 rounded-md grid place-items-center text-white font-serif text-2xl" style={{ backgroundColor: f.primary_color }}>
                {(f.brand_name || "?")[0]}
              </div>
            )}
            <div>
              <div className="font-serif text-2xl">{f.brand_name || "Your business"}</div>
              <div className="text-xs text-muted-foreground font-mono">{brandedUrl}</div>
            </div>
          </div>
        </Card>

        {/* Branding form */}
        <Card className="p-6 space-y-4" data-testid="tenant-branding-form">
          <div>
            <Label className="label-caps text-[10px]">Business name</Label>
            <Input value={f.brand_name} onChange={e => setF({ ...f, brand_name: e.target.value })} data-testid="tenant-brand-name" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-caps text-[10px]">Primary color</Label>
              <div className="flex gap-2 items-center">
                <input ref={colorInputRef} type="color" value={f.primary_color} onChange={e => setF({ ...f, primary_color: e.target.value })} className="h-10 w-14 rounded border border-input cursor-pointer" data-testid="tenant-color-picker" />
                <Input value={f.primary_color} onChange={e => setF({ ...f, primary_color: e.target.value })} className="font-mono" />
              </div>
            </div>
            <div>
              <Label className="label-caps text-[10px]">Contact phone</Label>
              <Input value={f.contact_phone} onChange={e => setF({ ...f, contact_phone: e.target.value })} placeholder="555-123-4567" data-testid="tenant-phone" />
            </div>
          </div>

          <div>
            <Label className="label-caps text-[10px]">Logo</Label>
            <div className="flex items-start gap-4">
              {f.logo_url && (
                <img src={f.logo_url} alt="" className="h-16 w-16 object-contain rounded-md border border-border bg-white p-1" />
              )}
              <div className="flex-1 space-y-2">
                <label>
                  <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" data-testid="tenant-logo-upload" onChange={e => uploadLogo(e.target.files)} />
                  <Button asChild variant="outline" disabled={uploading}>
                    <span><Upload size={12} className="mr-2" /> {uploading ? "Uploading…" : "Upload new logo"}</span>
                  </Button>
                </label>
                <Input value={f.logo_url} onChange={e => setF({ ...f, logo_url: e.target.value })} placeholder="…or paste a logo URL" className="text-xs" data-testid="tenant-logo-url" />
                <div className="text-[10px] text-muted-foreground">PNG, JPG, WEBP, or SVG · 5 MB max</div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-border">
            <Button size="lg" onClick={save} disabled={saving} data-testid="tenant-save-btn">
              {saving ? "Saving…" : <><CheckCircle2 size={14} className="mr-2" /> Save branding</>}
            </Button>
          </div>
        </Card>

        {/* Subdomain rename — buyers can pick a prettier URL any time */}
        <Card className="p-6 space-y-3" data-testid="tenant-subdomain-card">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={16} className="text-primary" />
            <div className="font-serif text-xl">Your subdomain</div>
          </div>
          <p className="text-sm text-muted-foreground">
            Change your public URL any time. Pick something short and memorable — like <code>janes-taxes</code> instead of <code>janes-smith-tax-services</code>.
          </p>
          <div>
            <Label className="label-caps text-[10px]">Subdomain</Label>
            <div className="flex items-center gap-2">
              <Input
                value={f.subdomain}
                onChange={e => {
                  const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                  setF({ ...f, subdomain: v });
                  setSubCheck(null);
                  if (v && v !== tenant.subdomain && v.length >= 2) {
                    setCheckingSub(true);
                    api.get(`/tenants/check-subdomain/${v}`)
                      .then(r => setSubCheck(r.data))
                      .catch(() => {})
                      .finally(() => setCheckingSub(false));
                  }
                }}
                placeholder="janes-taxes"
                minLength={2}
                maxLength={32}
                className="font-mono"
                data-testid="tenant-subdomain-input"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">.dynamictax.online</span>
            </div>
            {f.subdomain && f.subdomain !== tenant.subdomain && (
              <div className={`text-xs mt-2 flex items-center gap-1 ${
                checkingSub ? "text-muted-foreground" :
                subCheck?.available ? "text-primary" :
                subCheck ? "text-destructive" : "text-muted-foreground"
              }`} data-testid="tenant-subdomain-check">
                {checkingSub ? "Checking…" :
                 subCheck?.available ? <>✓ <code className="text-foreground">{subCheck.clean}.dynamictax.online</code> is yours if you save now</> :
                 subCheck ? <>✗ {subCheck.reason}</> : ""}
              </div>
            )}
            {f.subdomain === tenant.subdomain && (
              <div className="text-[11px] text-muted-foreground mt-2">
                Current URL: <code className="text-foreground">{tenant.subdomain}.dynamictax.online</code>
              </div>
            )}
          </div>
        </Card>

        {/* Custom domain */}
        <Card className="p-6 space-y-3" data-testid="tenant-custom-domain">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={16} className="text-primary" />
            <div className="font-serif text-xl">Custom domain</div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/20 text-accent">Premium look</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Point your own domain (like <code>janessmith.com</code>) at your tenant instead of using the <code>{tenant.subdomain}.dynamictax.online</code> subdomain. You buy the domain and set the DNS; we handle the routing.
          </p>
          <div>
            <Label className="label-caps text-[10px]">Your domain (no https)</Label>
            <Input value={f.custom_domain} onChange={e => setF({ ...f, custom_domain: e.target.value })} placeholder="taxes.janessmith.com" className="font-mono" data-testid="tenant-custom-domain-input" />
          </div>
          {f.custom_domain && (
            <div className="rounded-md bg-secondary/50 border border-border p-4 text-xs space-y-2">
              <div className="font-medium">DNS setup steps:</div>
              <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                <li>Log in to your registrar (Namecheap / GoDaddy / Squarespace).</li>
                <li>Add a <code>CNAME</code> record: <code className="text-foreground">{f.custom_domain}</code> → <code className="text-foreground">{tenant.subdomain}.dynamictax.online</code></li>
                <li>Save. DNS updates take 5-30 minutes.</li>
                <li>Come back here and hit save — SSL auto-provisions in ~2 min.</li>
              </ol>
            </div>
          )}
        </Card>

        {/* Your URL card */}
        <Card className="p-6" data-testid="tenant-url-card">
          <div className="label-caps text-[10px] mb-2 text-primary">Your public site</div>
          <div className="flex flex-wrap items-center gap-3">
            <a href={brandedUrl} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary underline break-all">{brandedUrl}</a>
            <Button size="sm" variant="outline" onClick={copyUrlAndMark} data-testid="tenant-url-copy-btn">
              <Copy size={11} className="mr-1" /> Copy
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={brandedUrl} target="_blank" rel="noreferrer"><ExternalLink size={11} className="mr-1" /> Visit</a>
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
